import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { getAppDisplayName } from '../../../apps/definition';
import { trackEvent } from '../../../extra/sharedMethods';
import { useQAController } from '../../../scripts/QAController';
import { AnalyticsEvent } from '../../../scripts/constants/analytics-event';
import {
  QuerySuggestion,
  useQuerySuggestions,
} from '../../../scripts/hooks/querySuggestions';
import { UIIcon } from '../../controls/ui/UIIcon/UIIcon';
import {
  Card,
  CardContent,
  CardFooter,
} from '../../shadcn/lib/components/card';
import { ConnectedApps } from '../QATextInputView';

export const QASuggestedQueries: FC<{
  shouldStream: boolean;
  onLastItemCompleted: () => void;
}> = ({ shouldStream, onLastItemCompleted }) => {
  const [currentChild, setCurrentChild] = useState(1);
  const { querySuggestions, loading } = useQuerySuggestions();

  const qaController = useQAController();

  const handleQuerySuggestionClick = useCallback(
    (querySuggestion: QuerySuggestion) => {
      const query = querySuggestion.suggested_query;
      const queryFilters = querySuggestion.app_filter;

      let baseLlm;
      let searchFilters: ConnectedApps[];

      if (
        queryFilters.length === 1 &&
        queryFilters[0]?.app_name === 'baseLlm'
      ) {
        baseLlm = true;
        searchFilters = [];
      } else if (
        queryFilters.length === 1 &&
        queryFilters[0]?.app_name === 'web'
      ) {
        baseLlm = false;
        searchFilters = [{ id: 0, appName: 'web', displayName: 'Web' }];
      } else {
        baseLlm = false;
        searchFilters = queryFilters.map((filter) => ({
          id: filter.app_id,
          appName: filter.app_name,
          displayName: getAppDisplayName(filter.app_name),
        }));
      }

      qaController.triggerEvent('setQueryAndFilter', {
        query,
        queryFilters: searchFilters,
        baseLlm,
      });

      trackEvent(
        AnalyticsEvent.QAOnboardingQuerySuggestionClicked,
        { searchFilters },
        {
          query,
        }
      );
    },
    [qaController]
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!shouldStream) {
      setCurrentChild(99);
      return;
    }

    const maxChildren = 3;
    let timeoutId: NodeJS.Timeout;

    if (currentChild < maxChildren) {
      timeoutId = setTimeout(() => {
        setCurrentChild((prevChild) => prevChild + 1);
      }, 1000);
    }

    if (currentChild === maxChildren) {
      trackEvent(
        AnalyticsEvent.QAOnboardingQuerySuggestionsShown,
        {},
        {
          querySuggestions,
        }
      );

      onLastItemCompleted();
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    loading,
    shouldStream,
    currentChild,
    onLastItemCompleted,
    querySuggestions,
  ]);

  if (loading) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="sm:max-w-3xl sm:mx-auto max-w-full mx-2 w-full px-4 flex flex-col gap-1">
        {currentChild >= 1 && (
          <div
            className={
              shouldStream ? 'animate-in fade-in ease-in-out duration-400' : ''
            }
          >
            {querySuggestions.length === 0
              ? "You're all set! Try asking a question below ⚡️"
              : 'After connecting your apps above, give these questions a try or enter one yourself below ⚡️'}
          </div>
        )}
        {currentChild >= 2 && (
          <div
            className={classNames('text-sm text-gray-50', {
              'animate-in fade-in ease-in-out duration-400': shouldStream,
            })}
          >
            Note: Dash AI is getting better but sometimes struggles with complex
            searches or when the words in your questions don't match the words
            in the documents exactly. Learn more about asking effective
            questions{' '}
            <a
              href="https://support.dashworks.ai/getting-started/ask-questions"
              rel="noreferrer"
              target="_blank"
            >
              here
            </a>
            .
          </div>
        )}
      </div>
      {currentChild >= 3 && querySuggestions.length !== 0 && (
        <div
          className={classNames('mt-2 flex gap-1.5', {
            'animate-in fade-in ease-in-out duration-400': shouldStream,
          })}
        >
          {querySuggestions.map((query) => (
            <Card
              className="flex-1 cursor-pointer flex flex-col hover:bg-gray-5 shadow-md border border-solid border-cloud-15 p-3"
              key={query.suggested_query}
              onClick={() => {
                handleQuerySuggestionClick(query);
              }}
            >
              <CardContent>{query.suggested_query}</CardContent>
              <CardFooter className="mt-auto flex justify-end">
                <UIIcon name="arrow-up-right" stroke />
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
